/**
 * Bundles on screen: links between groups drawn as one thick link with a count, which opens
 * into its links when the user looks at it ({@link Focus}). Which links form a bundle is
 * decided in `../bundles.ts`; this file builds the scene's bundles, says which are open, where
 * they are drawn, and which one is under the pointer.
 */
import { bundleLinks, parseEnd, type BundleEnd } from "../bundles";
import { segmentDistance } from "./geometry";
import type { Scene } from "./index";
import type { Focus, SceneBundle, SceneGroup, ScreenCircle, Segment, SimLink } from "./types";
import { MIN_BUNDLE_PX } from "./constants";

/** What the scene does with bundles. */
export interface SceneBundles {
  /** `true` when a bundle is drawn as its links instead of one line (see {@link Focus}). */
  isOpen(b: SceneBundle, focus: Focus): boolean;
  /** `true` when a link is drawn on its own: not bundled, in an open bundle, or touching the focused node. */
  linkDrawn(l: SimLink, focus: Focus): boolean;
  /** Screen segment of a bundle, from the rim of one end to the rim of the other. */
  bundleSegment(b: SceneBundle): Segment | null;
  /** The closed bundle under a screen point (its line or its count). */
  hitBundle(px: number, py: number, focus: Focus): SceneBundle | null;
  /** The closed bundle whose count is under a screen point. Counts are drawn above nodes, so they win. */
  hitBundleCount(px: number, py: number, focus: Focus): SceneBundle | null;
  /**
   * Says which bundles' counts the last picture drew (among many bundles not all are: see
   * `windows/graph/picture.ts`); only those can be clicked. `null`: every count is drawn.
   */
  countsDrawn(drawn: ReadonlySet<SceneBundle> | null): void;
}

/** The bundles of `scene`. */
export function bundlesOnScreen(scene: Scene): SceneBundles {
  /** `true` when group `g` is the end, or sits inside it or around it (a node end: holds it). */
  const relates = (g: SceneGroup, end: BundleEnd): boolean => {
    const { kind, id } = parseEnd(end);
    if (kind === "node") return g.all.some((n) => n.id === id);
    for (let at: SceneGroup | null = g; at; at = at.parent) if (at.id === id) return true;
    const other = scene.groupById.get(id);
    for (let at: SceneGroup | null = other?.parent ?? null; at; at = at.parent) if (at === g) return true;
    return false;
  };

  /** An end's circle on screen: the group's circle, or the node with a small margin. */
  const endCircle = (end: BundleEnd): ScreenCircle | null => {
    const { kind, id } = parseEnd(end);
    if (kind === "node") {
      const n = scene.byId.get(id);
      return n?.visible ? { x: scene.sx(n), y: scene.sy(n), r: scene.rs(n) + 3 } : null;
    }
    const g = scene.groupById.get(id);
    return g?.visible ? scene.groupCircle(g) : null;
  };

  /** The bundles whose counts the last picture drew (`null`: all of them). */
  let drawn: ReadonlySet<SceneBundle> | null = null;
  /** `true` when the screen point is on the count of `b`, drawn in the middle of segment `s` (when it is drawn). */
  const onCount = (px: number, py: number, b: SceneBundle, s: Segment) => (!drawn || drawn.has(b)) && Math.hypot(px - (s.x1 + s.x2) / 2, py - (s.y1 + s.y2) / 2) <= 11;

  return {
    countsDrawn(counts) {
      drawn = counts;
    },

    isOpen(b, f) {
      if (f.bundle === b || (f.link && scene.bundleOf.get(f.link) === b)) return true;
      return !!f.group && (relates(f.group, b.from) || relates(f.group, b.to));
    },

    linkDrawn(l, f) {
      const b = scene.bundleOf.get(l);
      return !b || scene.isOpen(b, f) || (!!f.node && (l.a === f.node || l.b === f.node));
    },

    bundleSegment(b) {
      const a = endCircle(b.from);
      const c = endCircle(b.to);
      if (!a || !c) return null;
      const dx = c.x - a.x;
      const dy = c.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1) return null;
      const ux = dx / len;
      const uy = dy / len;
      // Shifted a little to the right of its direction, so A → B and B → A do not overlap.
      const ox = -uy * 4;
      const oy = ux * 4;
      // When the rims are close, reach into both circles so the bundle stays long enough to read.
      const inset = Math.max(0, (MIN_BUNDLE_PX - (len - a.r - c.r)) / 2);
      const ra = Math.max(a.r - Math.min(inset, a.r * 0.6), 0);
      const rc = Math.max(c.r - Math.min(inset, c.r * 0.6), 0);
      if (len - ra - rc < 12) return null;
      return { x1: a.x + ux * ra + ox, y1: a.y + uy * ra + oy, x2: c.x - ux * rc + ox, y2: c.y - uy * rc + oy };
    },

    hitBundleCount(px, py, f) {
      for (const b of scene.bundles) {
        if (scene.isOpen(b, f)) continue;
        const s = scene.bundleSegment(b);
        if (s && onCount(px, py, b, s)) return b;
      }
      return null;
    },

    hitBundle(px, py, f) {
      let best: SceneBundle | null = null;
      let bestDistance = 8;
      for (const b of scene.bundles) {
        if (scene.isOpen(b, f)) continue;
        const s = scene.bundleSegment(b);
        if (!s) continue;
        const d = onCount(px, py, b, s) ? 0 : segmentDistance(px, py, s.x1, s.y1, s.x2, s.y2);
        if (d < bestDistance) {
          bestDistance = d;
          best = b;
        }
      }
      return best;
    },
  };
}

/** Rebuilds the scene's bundles from its visible links (or clears them when `on` is false). */
export function computeBundles(scene: Scene, on: boolean): void {
  const groupOf = (id: string) => {
    const g = scene.byId.get(id)?.node.group;
    return g && scene.groupById.has(g) ? g : null;
  };
  const parentOf = (id: string) => scene.groupById.get(id)?.parent?.id ?? null;
  const visible = on && scene.groups.length ? scene.links.filter((l) => l.visible) : [];
  scene.bundles = bundleLinks(visible, (l) => l.link, groupOf, parentOf).map((b) => ({
    ...b,
    kinds: [...new Set(b.links.map((l) => l.link.kind))],
  }));
  scene.bundleByKey = new Map(scene.bundles.map((b) => [b.key, b]));
  scene.bundleOf = new Map(scene.bundles.flatMap((b) => b.links.map((l) => [l, b] as const)));
}
